import { useEffect, useState, useCallback, useRef } from 'react'
import { initializeCollections } from '../store/collections'
import { initializeDatabase, isDatabaseInitialized } from '../services/TauriDatabase'
import { blockEventBus } from '../services/BlockEventBus'
import { startCacheReconciliation, stopCacheReconciliation } from '../services/reconciliationScheduler'

interface UseElectricSyncOptions {
  userId: string | null
  enabled?: boolean
}

interface UseElectricSyncReturn {
  isInitialized: boolean
  isSyncing: boolean
  isConnected: boolean
  error: Error | null
  lastSyncAt: Date | null
  reinitialize: () => Promise<void>
}

// 最大重试次数：增加到 5 次，覆盖更多失败场景
const MAX_RETRY_COUNT = 5

// ★ 会话级健康巡检配置（已放行后服务不可达的主动提示）
// ShapeStream 对网络层失败（连接拒绝/DNS/服务宕机）静默指数退避重试，
// 不触发 onError / status:change —— 会话中途服务不可达时 UI 毫无感知。
// 巡检周期性探测数据服务健康端点，弥补该盲区。
const HEALTH_PATROL_INTERVAL_MS = 60_000    // 正常态巡检间隔
const HEALTH_PATROL_FAST_MS = 20_000       // 失败态巡检间隔（加速恢复检测）
const HEALTH_PATROL_FAILURES = 2           // 连续失败达到该次数才判定不可达（防单次抖动误报）
const HEALTH_PATROL_TIMEOUT_MS = 5_000     // 单次健康检查超时

/**
 * React Hook: 初始化 TanStack DB + Electric 同步
 */
export function useElectricSync({
  userId,
  enabled = true,
}: UseElectricSyncOptions): UseElectricSyncReturn {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null)

  const isInitializingRef = useRef(false)
  const lastUserIdRef = useRef<string | null>(null)
  // isInitialized 的 ref 镜像，供 online/offline 回调读取最新值，避免 stale closure
  const isInitializedRef = useRef(false)
  useEffect(() => { isInitializedRef.current = isInitialized }, [isInitialized])
  // 有界自动重试：瞬时失败（SQLite 锁定/IPC 抖动）最多自动重试 5 次（指数退避），避免永久骨架屏
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ★ 会话级健康巡检状态（详见顶部常量注释）：
  // - failureStreak：连续失败计数（成功即清零）
  // - patrolOff：当前是否处于"巡检已判定不可达"态（恢复时才广播，避免成功巡检的无效广播）
  const healthFailureStreakRef = useRef(0)
  const healthPatrolOffRef = useRef(false)

  const initialize = useCallback(async () => {
    if (!userId || !enabled) {
      console.log('[useElectricSync] 跳过初始化:', { userId: !!userId, enabled })
      return
    }

    if (isInitializingRef.current) {
      console.log('[useElectricSync] 初始化正在进行中，跳过')
      return
    }

    // Electric + TanStack DB 最佳实践：
    // 刷新页面后需要重新初始化 Collection，因为模块级变量会丢失
    // 检查 Collection 是否真的已初始化，而不是仅依赖 ref
    try {
      const { getNotesCollection, getCollectionsUserId } = await import('../store/collections')
      getNotesCollection()  // 未初始化时抛异常，进入 catch 走初始化路径
      // userId 一致性校验：main.tsx 预热可能使用了 localStorage 中的脏 userId，
      // 与当前会话 userId 不匹配时必须继续走 initializeCollections 重建集合
      if (getCollectionsUserId() === userId) {
        // eager 模式 + SQLite persistence：Collection 创建时已自动从本地缓存水合并启动同步，
        // 数据通过 subscribeChanges 推送路径自动到达 Redux，无需手动 preload/强制推送
        console.log('[useElectricSync] Collection 已存在（同用户），跳过重复初始化')

        // 确保 BlockEventBus 已启动（幂等操作，已启动则直接返回）
        blockEventBus.start().catch(e => console.warn('[useElectricSync] BlockEventBus 启动失败:', e))
        setIsInitialized(true)
        setIsConnected(true)
        setLastSyncAt(new Date())
        retryCountRef.current = 0  // 重置重试计数
        return
      }
      console.log('[useElectricSync] Collection userId 不匹配（预热 userId 已过期），重新初始化')
    } catch {
      console.log('[useElectricSync] Collection 未初始化，开始初始化...')
    }

    isInitializingRef.current = true
    lastUserIdRef.current = userId

    try {
      setIsSyncing(true)
      setError(null)

      console.log('[useElectricSync] 开始初始化，用户ID:', userId)

      console.log('[useElectricSync] 并行初始化：Tauri 数据库 + TanStack DB 集合')
      const INIT_TIMEOUT = 15000
      await Promise.all([
        initializeDatabase().then(() => {
          if (!isDatabaseInitialized()) {
            console.warn('[useElectricSync] 数据库初始化标记未设置，但继续（内存模式）')
          }
        }),
        Promise.race([
          initializeCollections(userId),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('初始化超时（15s）')), INIT_TIMEOUT)
          ),
        ]),
      ])
      console.log('[useElectricSync] 并行初始化完成')

      // 步骤 2.5: 启动 BlockEventBus（确保 Collection 订阅在应用启动时就绑定）
      // 消除懒启动竞态风险，避免错过早期变更事件
      await blockEventBus.start()
      console.log('[useElectricSync] 步骤 2.5 完成: BlockEventBus 启动完成')

      // 步骤 2.6: 启动跨设备头像同步订阅（fire-and-forget，不阻塞主流程）
      // 一端更新头像 → user_avatars 表 updated_at 变更 → 本设备经 Electric Shape 收到 → 强制刷新头像缓存
      try {
        const { initializeAvatarSyncCollection } = await import('../store/collections/avatarSyncCollection')
        void initializeAvatarSyncCollection(userId).catch((e: any) => {
          console.warn('[useElectricSync] 头像同步集合初始化失败（非致命）:', e)
        })
      } catch (e) {
        console.warn('[useElectricSync] 头像同步集合模块加载失败:', e)
      }

      // 步骤 2.65: 启动跨设备锁密码同步订阅（fire-and-forget，不阻塞主流程）
      // 一端设置/清除密码 → user_lock_settings 表变更 → 本设备经 Electric Shape 收到 → 更新本地镜像
      try {
        const { initializeLockSettingsCollection } = await import('../store/collections/lockSettingsCollection')
        void initializeLockSettingsCollection(userId).catch((e: any) => {
          console.warn('[useElectricSync] 锁密码同步集合初始化失败（非致命）:', e)
        })
      } catch (e) {
        console.warn('[useElectricSync] 锁密码同步集合模块加载失败:', e)
      }

      // 注意：已移除步骤 3（同步用户 ID 到 Rust 后端）
      // 因为我们已经移除了 Rust SQLite，不需要再同步用户 ID
      // 用户 ID 通过 Electric Shape 的 where 子句过滤
      console.log('[useElectricSync] 步骤 3: 用户 ID 通过 Electric Shape 过滤，无需同步到 Rust 后端')

      setIsInitialized(true)
      setIsConnected(true)
      setLastSyncAt(new Date())
      retryCountRef.current = 0  // 重置重试计数
      console.log('[useElectricSync] 初始化全部完成')
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      const errorMsg = error.message || ''

      // 错误分类：网络/认证/数据库/未知，对应不同恢复策略
      const isNetworkError = /network|fetch|CORS|timeout|ECONNREFUSED|ENOTFOUND|abort/i.test(errorMsg)
      const isAuthError = /401|403|unauthorized|forbidden|token|认证|授权/i.test(errorMsg)
      const isDatabaseError = /database|sqlite|locked|constraint|disk|数据库/i.test(errorMsg)

      console.error('[useElectricSync] 初始化失败（分类）:', {
        message: errorMsg,
        isNetworkError,
        isAuthError,
        isDatabaseError,
      })
      setError(error)

      // 检查 Collection 是否已部分创建（部分集合失败时 notesCollection 可能已可用）
      try {
        const { getNotesCollection } = await import('../store/collections')
        const collection = getNotesCollection()
        if (collection) {
          console.warn('[useElectricSync] 初始化部分失败，但 notesCollection 已就绪，标记为已初始化')
          setIsInitialized(true)
          setIsConnected(true)
          setError(null)
          setLastSyncAt(new Date())
          retryCountRef.current = 0
        } else {
          // 降级模式：notesCollection 为 null 但已标记初始化
          console.warn('[useElectricSync] 降级模式：notesCollection 不可用，标记为已初始化')
          setIsInitialized(true)
          setIsConnected(false)
          setError(null)
          retryCountRef.current = 0
        }
      } catch {
        console.error('[useElectricSync] notesCollection 未创建，初始化完全失败')
        setIsConnected(false)

        // 恢复策略按错误类型分流
        const shouldRetry = (isNetworkError || (!isAuthError && !isDatabaseError)) && retryCountRef.current < MAX_RETRY_COUNT
        if (shouldRetry) {
          const delay = Math.pow(2, retryCountRef.current) * 1000
          retryCountRef.current++
          const reason = isNetworkError ? '网络错误' : '未知错误'
          console.log(`[useElectricSync] ${reason}，${delay}ms 后自动重试（第 ${retryCountRef.current}/${MAX_RETRY_COUNT} 次）`)
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null
            initialize()
          }, delay)
        } else if (isAuthError) {
          // 认证错误：不自动重试，由 UI 引导用户重新登录
          console.error('[useElectricSync] 认证失败，请重新登录')
        } else if (isDatabaseError) {
          // 数据库错误：不自动重试，提示用户重启应用
          // CANTOPEN（unable to open）/ readonly 多为目录权限问题（如开发工具沙箱），
          // 重启无效——给出可操作的排查提示
          const isPermissionIssue = /unable to open|readonly|Operation not permitted|permission/i.test(errorMsg)
          if (isPermissionIssue) {
            console.error(
              '[useElectricSync] 数据库无法写入（疑似目录权限/沙箱限制）：' +
              '请确认应用数据目录可写（~/Library/Application Support/com.lingshinote.yanwu 或 Android 对应目录）。' +
              '若从受限终端启动 dev，请改用系统终端运行 npm run dev:desktop'
            )
          } else {
            console.error('[useElectricSync] 数据库错误，请重启应用')
          }
        } else {
          console.error(`[useElectricSync] 已达到最大重试次数 (${MAX_RETRY_COUNT})，不再自动重试`)
          console.warn('[useElectricSync] 用户需要手动重启应用或点击重试按钮')
        }
      }
      isInitializingRef.current = false
      lastUserIdRef.current = null
    } finally {
      setIsSyncing(false)
      isInitializingRef.current = false
    }
  }, [userId, enabled])

  // 主初始化 effect：仅在 userId/enabled 变化时触发，不依赖 isInitialized
  // 避免 isInitialized 变化导致 online/offline 监听器重新绑定（丢失事件风险）
  useEffect(() => {
    // userId 变化（登出/切换用户）时重置初始化状态
    // cleanupCollections() 只重置模块级变量，不重置本 hook 的 refs，需在此补充
    if (isInitializedRef.current && lastUserIdRef.current !== userId) {
      console.log('[useElectricSync] userId 变化，重置初始化状态:', lastUserIdRef.current, '→', userId)
      setIsInitialized(false)
      isInitializedRef.current = false
      isInitializingRef.current = false
      retryCountRef.current = 0
    }

    if (isInitializedRef.current && userId) {
      console.log('[useElectricSync] 已初始化，跳过初始化调用')
      return
    }

    initialize()

    // 启动运行期对账监控：userId 就绪即启动（对账内部自带云快照确认 + 冷却 + 次数上限）
    // 在初始化 effect 内启动，userId 变化（登出/切换）时随 cleanup 停止、随重跑重启
    if (userId && enabled) {
      startCacheReconciliation(userId)
    }

    // 监听浏览器在线/离线——使用 ref 读取最新 isInitialized，避免 stale closure
    const handleOnline = () => {
      console.log('[useElectricSync] 浏览器上线')
      if (isInitializedRef.current) {
        setIsConnected(true)
      } else if (userId && enabled) {
        console.log('[useElectricSync] 网络恢复，触发初始化重试')
        retryCountRef.current = 0
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current)
          retryTimerRef.current = null
        }
        initialize()
      }
    }
    const handleOffline = () => {
      console.log('[useElectricSync] 浏览器离线')
      setIsConnected(false)
    }
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    if (!navigator.onLine) {
      setIsConnected(false)
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      // 停止对账监控（userId 变化 / 组件卸载时）
      stopCacheReconciliation()
    }
  }, [initialize, userId, enabled])

  // ★ 会话级健康巡检（已放行后服务不可达的主动提示，弥补 ShapeStream 静默重试盲区）：
  // - 仅 isInitialized 后启动（未初始化时 useElectricSync 自身的初始化重试已覆盖）
  // - 设备离线（!navigator.onLine）时跳过当轮（离线提示由 online/offline 事件负责），不停止巡检
  // - 连续 HEALTH_PATROL_FAILURES 次失败才判定不可达（单次网络抖动不误报）：
  //   广播 false + setIsConnected(false) → 消费方（App.tsx toast / SujiPage 提示）立即感知
  // - 恢复：广播 true + setIsConnected(true) 清除提示；失败态用快速间隔加速恢复检测
  // - 与 SujiPage 加载期巡检（5s 高频）互补：本巡检覆盖整个会话期，就绪后低频运行
  useEffect(() => {
    if (!isInitialized || !userId || !enabled) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const patrol = async () => {
      if (cancelled) return
      if (navigator.onLine) {
        try {
          const { getApiBaseUrl } = await import('../store/collections')
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), HEALTH_PATROL_TIMEOUT_MS)
          try {
            const resp = await fetch(`${getApiBaseUrl()}/api/sync/health`, { signal: controller.signal })
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
          } finally {
            clearTimeout(timeoutId)
          }
          // 健康成功：清零计数；仅此前处于判定不可达态才广播恢复（避免无效广播）
          healthFailureStreakRef.current = 0
          if (healthPatrolOffRef.current) {
            healthPatrolOffRef.current = false
            console.log('[useElectricSync] 会话级巡检：数据服务已恢复')
            const { checkElectricAvailableOnce } = await import('../store/collections')
            void checkElectricAvailableOnce()  // 广播 true（消费方清除提示）
            setIsConnected(true)
          }
        } catch {
          healthFailureStreakRef.current++
          if (
            healthFailureStreakRef.current >= HEALTH_PATROL_FAILURES &&
            !healthPatrolOffRef.current
          ) {
            healthPatrolOffRef.current = true
            console.warn('[useElectricSync] 会话级巡检：数据服务连续不可达，标记同步离线')
            const { checkElectricAvailableOnce } = await import('../store/collections')
            void checkElectricAvailableOnce()  // 广播 false（消费方提示）
            setIsConnected(false)
          }
        }
      }
      if (!cancelled) {
        // 已有失败迹象（streak>0 或已判定）用快速间隔：不可达判定 ~80s 内完成，恢复检测 ~20s
        const fast = healthPatrolOffRef.current || healthFailureStreakRef.current > 0
        timer = setTimeout(patrol, fast ? HEALTH_PATROL_FAST_MS : HEALTH_PATROL_INTERVAL_MS)
      }
    }

    timer = setTimeout(patrol, HEALTH_PATROL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [isInitialized, userId, enabled])

  // 订阅 effect：isInitialized 变为 true 时订阅 Collection 变更与状态
  // 独立于主初始化 effect，避免初始化重跑时丢失订阅
  useEffect(() => {
    if (!isInitialized) return

    let unsubscribeChanges: (() => void) | null = null
    let unsubscribeStatus: (() => void) | null = null

    const subscribeToCollection = async () => {
      try {
        const { getNotesCollection } = await import('../store/collections')
        const collection = getNotesCollection()

        const subscription = collection.subscribeChanges((changes: any[]) => {
          if (changes.length > 0) {
            setLastSyncAt(new Date())
            if (navigator.onLine) {
              setIsConnected(true)
            }
          }
        }, { includeInitialState: false })
        unsubscribeChanges = () => subscription.unsubscribe()

        unsubscribeStatus = collection.on('status:change', (event: any) => {
          console.log('[useElectricSync] Collection 状态变化:', event.previousStatus, '→', event.status)
          if (event.status === 'error') {
            setIsConnected(false)
          } else if (event.status === 'connected' || event.status === 'syncing') {
            setIsConnected(true)
            setLastSyncAt(new Date())
          }
        })
      } catch {
        // Collection 未就绪
      }
    }
    subscribeToCollection()

    return () => {
      if (unsubscribeChanges) unsubscribeChanges()
      if (unsubscribeStatus) unsubscribeStatus()
    }
  }, [isInitialized])

  const reinitialize = useCallback(async () => {
    console.log('[useElectricSync] 手动重新初始化')
    setIsInitialized(false)
    setError(null)
    retryCountRef.current = 0  // 重置重试计数
    isInitializingRef.current = false
    lastUserIdRef.current = null
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    await initialize()
  }, [initialize])

  return {
    isInitialized,
    isSyncing,
    isConnected,
    error,
    lastSyncAt,
    reinitialize,
  }
}
